import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useMemo } from "react";
import { FaExpandAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  BseDto,
  useListTextDocumentsForNegotiationRoundQuery,
  useListTextDocumentsForSourcingEventQuery,
} from "../../../../autogen/bff-api";
import { useSub } from "../../../../common/subscription/useSub";
import { urls } from "../../../../urls";
import { requirePublishedOrAwardedState } from "../../requirePublishedOrAwardedState";
import { BseReports } from "./BseReports";
import { Documents } from "./Documents";
import { Intro } from "./Intro";
import { useNegotiation } from "./NegotiationContext";

const requireDescription = (bse: BseDto): string => {
  const fields = requirePublishedOrAwardedState(bse);
  return fields.description;
};

export const Request = ({ event }: { event: BseDto }) => {
  const navigate = useNavigate();
  const subscriptions = useSub();
  const { currentRound } = useNegotiation();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: eventTextDocRes, isLoading: isLoadingEventTextDocuments } = useListTextDocumentsForSourcingEventQuery({
    eventId: event.id,
  });

  const { data: roundTextDocRes, isLoading: isLoadingRoundTextDocuments } =
    useListTextDocumentsForNegotiationRoundQuery(currentRound ? { negotiationRoundId: currentRound.id } : skipToken);

  const uploadedDocuments = useMemo(() => {
    const eventDocs = event?.documents ?? [];
    const roundDocs = currentRound?.documents ?? [];

    return currentRound ? roundDocs : eventDocs;
  }, [event?.documents, currentRound]);

  const textDocuments = useMemo(() => {
    return currentRound ? (roundTextDocRes?.documents ?? []) : (eventTextDocRes?.documents ?? []);
  }, [eventTextDocRes?.documents, roundTextDocRes?.documents, currentRound]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl">
        <ModalOverlay />
        <ModalContent maxH="80vh" overflowY="auto">
          <ModalHeader>{t("Description")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Intro
              description={currentRound ? (currentRound?.description ?? "") : event ? requireDescription(event) : ""}
              alwaysExpanded={true}
              documents={currentRound ? currentRound.descriptionDocuments : event.descriptionDocuments}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Flex columnGap="5" width="full" maxWidth="full" overflowX="hidden">
        <Box width="calc(50% - 10px)" maxWidth="calc(50% - 10px)" minWidth="calc(50% - 10px)" pt="5">
          <Flex width="full" alignItems="center" justifyContent="space-between" mb="2">
            <Text fontSize="sm" fontWeight="semibold">
              {t("Description")}
            </Text>
            <IconButton
              aria-label={t("Expand description")}
              icon={<Icon as={FaExpandAlt} />}
              size="xs"
              variant="ghost"
              onClick={onOpen}
            />
          </Flex>
          <Intro
            description={currentRound ? (currentRound?.description ?? "") : event ? requireDescription(event) : ""}
            documents={currentRound ? currentRound.descriptionDocuments : event.descriptionDocuments}
          />
        </Box>
        <Flex
          width="calc(50% - 10px)"
          maxWidth="calc(50% - 10px)"
          minWidth="calc(50% - 10px)"
          flexDirection="column"
          alignItems="end"
          py="5"
        >
          {subscriptions.hasProjectAccess && (
            <Flex flexDirection="column" alignItems="end" fontWeight="semibold" width="full" maxWidth="full">
              <Text fontSize="sm" pb="2">
                {t("Projects")}
              </Text>
              <Box width="full" maxWidth="full">
                {event?.projects?.map((p) => (
                  <Button
                    key={p.id}
                    size="xs"
                    textAlign="right"
                    display="inline-block"
                    width="full"
                    maxWidth="full"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    onClick={() => navigate(urls.projects.view.go(p.id))}
                  >{`${p.externalId ? `${p.externalId} - ` : ""}${p.name}`}</Button>
                ))}
              </Box>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex width="full" maxWidth="full" overflow="hidden" rounded="md" flexDirection="column" pt="8">
        <Text fontSize="sm" fontWeight="semibold" pb="2">
          {t("Documents")}
        </Text>
        <Documents
          uploadedDocuments={uploadedDocuments}
          textDocuments={textDocuments}
          isLoading={isLoadingEventTextDocuments || isLoadingRoundTextDocuments}
        />
      </Flex>
      {(event.stateName === "Closed" || event.stateName === "Cancelled") && (
        <Box mt="6">
          <BseReports data={event} />
        </Box>
      )}
    </>
  );
};
